import {
  AfterViewInit, Component, ElementRef, EventEmitter, Injector, Input, OnChanges,
  OnDestroy, OnInit, Output, SimpleChanges, ViewChild
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import * as L from 'leaflet';
import 'src/assets/map-control/L.Control.ZoomBar.js';
import 'src/assets/js/maplibs/L.TileLayer.NoGap.js';
import { DeviceDetectorService } from 'ngx-device-detector';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import {
  AgricultureMapConstants, BASE_LAYER_LIST, COMPONENT_ROUTE_MAPPING, LAYER_TYPES,
  MAP_OPTIONS, MOBILE_MAP_OPTIONS
} from 'src/app/main/constants/dims-map-constants';
import { GisdataService } from 'src/app/main/services/gisdata.service';
import { MapService } from 'src/app/utils/map.service';
import { UtilsService } from 'src/app/utils/utils.service';

export type SrklMode = 'daily' | '3hourly' | 'lightning' | 'lightning3h' | 'wind' | 'wind3h';

export interface SrklScaleItem {
  max: number;          // class applies while value < max (last class: Infinity)
  color: string;
  label: string;        // legend text, e.g. "2.5 – 15.6"
  textColor?: string;   // text colour on top of `color` (tables, ranking, legend)
}

/** one option in the hover popup of a weather button (Daily / 3-Hourly). mode = null: not available yet, click does nothing. */
export interface SrklPeriod {
  label: string;
  mode: SrklMode | null;
}

/** one weather button (Rainfall, Lightning, Wind Speed ...) with the periods shown in its hover popup */
export interface SrklModeGroup {
  key: string;
  label: string;
  periods: SrklPeriod[];
}

/**
 * Srikakulam map widget. Same host contract as gis-dims-map (Leaflet, otherOptions,
 * injected map service, selectedParams subscription) but with no header / side-nav /
 * split / info-box: the page owns everything outside the map, and the map sizes itself
 * from --srkl-map-height instead of 100vh.
 *
 * The playbar is built in (cells show labelStrings[].label / .sub directly), so step keys
 * like "h0" / "f0" work; they do not have to be epoch timestamps.
 */
@Component({
  selector: 'gis-srkl-map',
  templateUrl: './gis-srkl-map.component.html',
  styleUrls: ['./gis-srkl-map.component.scss']
})
export class GisSrklMapComponent implements OnInit, AfterViewInit, OnChanges, OnDestroy {

  /** { serviceType, functionName, animationLayers, paramsComponent, hideGeoSearch, hooks... } */
  @Input() otherOptions: any = {};
  /** Published to UtilsService.selectedParams once the map is ready (e.g. SRIKAKULAM_PARAMS) */
  @Input() defaultParams: any;

  // optional self-drawn choropleth (use instead of / besides service layers)
  @Input() geojson: any;
  @Input() values: Record<string, number> = {};
  @Input() idProp = 'block_id';
  @Input() nameProp = 'block_name';
  @Input() pilotIds: string[] = [];
  @Input() selectedId: string | null = null;
  @Input() tooltipFn: ((id: string, name: string, value: number | undefined) => string) | null = null;
  @Input() showLabels = true;

  // legend / toggle / playbar
  @Input() scale: SrklScaleItem[] = [];
  @Input() legendTitle = 'mm per day';
  @Input() mode: SrklMode = 'daily';
  @Input() modes: { key: SrklMode; label: string }[] = [
    { key: 'daily', label: 'Daily' },
    { key: '3hourly', label: '3-hourly' },
    { key: 'lightning', label: 'Lightning' }
  ];
  /**
   * The weather buttons shown on the map. Hovering one opens its Daily / 3-Hourly popup.
   * Rainfall keeps the existing daily / 3hourly modes, Lightning the daily / 3-hourly lightning modes,
   * Wind Speed the daily (max gust) / 3-hourly (gust) wind modes.
   * Further types are added here later: mode null = not available yet.
   */
  /** false = the page shows its own parameter / time step bar, so the weather buttons on the map are hidden */
  @Input() showModeBar = true;
  @Input() modeGroups: SrklModeGroup[] = [
    {
      key: 'rainfall', label: 'Rainfall', periods: [
        { label: 'Daily', mode: 'daily' },
        { label: '3-Hourly', mode: '3hourly' }
      ]
    },
    {
      key: 'lightning', label: 'Lightning', periods: [
        { label: 'Daily', mode: 'lightning' },
        { label: '3-Hourly', mode: 'lightning3h' }
      ]
    },
    {
      key: 'wind', label: 'Wind Speed', periods: [
        { label: 'Daily', mode: 'wind' },
        { label: '3-Hourly', mode: 'wind3h' }
      ]
    }
  ];
  @Input() labelStrings: any[] = [];
  @Input() activeStep: string;

  @Input() title = '';
  @Input() timestampLabel = '';
  /** the page's data is still loading: shows the shimmer over the map (same as showThrobber) */
  @Input() loading = false;
  @Input() center: [number, number] = [18.3, 83.9];
  @Input() zoom = 9;
  @Input() showSearch = false;

  @Output() modeChange = new EventEmitter<SrklMode>();
  @Output() stepChange = new EventEmitter<string>();
  @Output() blockHover = new EventEmitter<string | null>();
  @Output() blockClick = new EventEmitter<string>();
  @Output() mapReady = new EventEmitter<GisSrklMapComponent>();

  @ViewChild('mapHost', { static: true }) mapHost: ElementRef;

  // ---- members GisdataService / the map service expect on the map component (mirrors gis-dims-map)
  map: any;
  injectedService: any;
  params: any = {};
  queryParams: any = {};
  routeParams: any = { component: null, dashboard: null };
  componentType: any;
  url: string;
  layers: any = {
    baselayers: {}, overlaylayers: {}, heatmaplayers: {}, stationlayers: {},
    stationlayersQC: [], aoiLayers: {}, processedLayers: {}, clickedBoundaryLayers: {}
  };
  activeLayers: any = {};
  legendList: any = {};
  selectedLocType: any;
  deviceInfo: any;
  homeBounds: any = null;
  pendingMobileAutoFit = false;
  showThrobber = false;
  blockNumber = 0;
  playPosition = 0;
  playFastSlow = false;
  playButtonDisable = true;
  activeSideMenu: any;

  // built-in legend / playbar state
  legendOpen = true;
  playing = false;
  private timer: any;
  private refitTimer: any;
  private userMoved = false;

  private geoLayer: any;
  private labelLayer: any;
  private resizeObs: any;
  private fitted = false;
  private destroy = new Subject<boolean>();

  constructor(
    private injector: Injector,
    private router: Router,
    private route: ActivatedRoute,
    private deviceService: DeviceDetectorService,
    public gisdataService: GisdataService,
    public mapService: MapService,
    private gisUtils: UtilsService
  ) {
    this.deviceInfo = this.deviceService.getDeviceInfo();
  }

  // ------------------------------------------------------------------ lifecycle
  ngOnInit(): void {
    this.playButtonDisable = !this.labelStrings || this.labelStrings.length === 0;
  }

  ngAfterViewInit(): void {
    this.initializeMapContainer();
  }

  ngOnChanges(c: SimpleChanges): void {
    if (c['labelStrings']) {
      this.playButtonDisable = !this.labelStrings || this.labelStrings.length === 0;
      this.stopPlay();
    }
    if (!this.map) { return; }
    if (c['geojson']) {
      this.fitted = false;
      this.renderGeo();
    } else if (c['values'] || c['scale'] || c['pilotIds'] || c['selectedId']) {
      this.refreshStyles();
    }
    if (c['showLabels']) { this.renderLabels(); }
    if (c['activeStep'] || c['labelStrings']) { this.syncPlaybar(); }
  }

  ngOnDestroy(): void {
    this.stopPlay();
    clearTimeout(this.refitTimer);
    if (this.resizeObs) { this.resizeObs.disconnect(); }
    if (this.otherOptions.hooks && this.otherOptions.hooks.destroy && this.injectedService) {
      this.injectedService[this.otherOptions.hooks.destroy](this);
    }
    // same reset as gis-dims-map so stale params never leak into the next module
    this.gisUtils.setSelectedParams({});
    sessionStorage.removeItem('params');
    this.destroy.next(true);
    this.destroy.complete();
    if (this.map) { this.map.remove(); this.map = null; }
  }

  // ------------------------------------------------------------------ map init
  isMobileDevice(): boolean {
    return this.deviceInfo['deviceType'] === 'mobile';
  }

  private initializeMapContainer(): void {
    this.updateRouteParams();
    this.componentType = this.routeParams.component;
    this.queryParams = { params: Object.assign({}, this.route.snapshot.queryParams) };

    const base = this.isMobileDevice() ? MOBILE_MAP_OPTIONS : MAP_OPTIONS;
    // created inside Angular's zone on purpose: GisdataService click handlers publish
    // selectedParams and the page must see those changes.
    this.map = (L.map as any)(this.mapHost.nativeElement, Object.assign({}, base, { editable: true }));
    this.map.setView(this.center, this.zoom);
    this.createPanes();
    this.addZoomBar();

    const hasService = !!this.otherOptions.serviceType;
    if (!hasService) {
      this.getAndSetBaseLayers(Number(localStorage.getItem('selectedBaseMapIndex')) || 0);
    }

    // once the user pans/zooms themselves we stop auto-refitting
    ['wheel', 'mousedown', 'touchstart'].forEach(ev =>
      this.mapHost.nativeElement.addEventListener(ev, () => { this.userMoved = true; }, { passive: true }));

    const RO = (window as any).ResizeObserver;
    if (RO) {
      this.resizeObs = new RO(() => {
        if (!this.map) { return; }
        this.map.invalidateSize();
        clearTimeout(this.refitTimer);
        this.refitTimer = setTimeout(() => this.refit(), 250);   // layout settled: fit the outline again
      });
      this.resizeObs.observe(this.mapHost.nativeElement);
    }

    if (hasService) {
      this.injectedService = this.injector.get(this.otherOptions.serviceType);
    }
    this.subscribeParams();
    this.renderGeo();
    this.syncPlaybar();

    // deferred so bindings that change as a result don't trip ExpressionChangedAfterItHasBeenChecked
    setTimeout(() => {
      if (this.defaultParams) { this.gisUtils.setSelectedParams(Object.assign({}, this.defaultParams)); }
      this.mapReady.emit(this);
    });
  }

  updateRouteParams(): void {
    this.url = this.router.url.split('?')[0];
    const seg = this.url.split('/');
    this.routeParams.component = seg[seg.length - 1];
    this.routeParams.dashboard = seg[seg.length - 2];
  }

  private subscribeParams(): void {
    this.gisUtils.getSelectedParams().pipe(takeUntil(this.destroy)).subscribe((params: any) => {
      if (this.gisdataService.isEmptyObj(params)) { return; }
      const mine = COMPONENT_ROUTE_MAPPING[params['component']] === this.routeParams.component
        || (this.otherOptions.paramsComponent && params['component'] === this.otherOptions.paramsComponent);
      if (mine) {
        this.params = JSON.parse(JSON.stringify(params));
        this.initMethods();
      }
    });
  }

  initMethods(): void {
    if (this.params['actionType'] === 'filter' || this.otherOptions.hasFilter) {
      this.userMoved = false;
      this.removeAllLayers();
      this.removeComponentWiseLayers();
      this.removeHeatMapLayers();
      if (this.injectedService && this.otherOptions.functionName) {
        this.injectedService[this.otherOptions.functionName](this);
      }
    }
  }

  private createPanes(): void {
    (AgricultureMapConstants.PANE_LIST || []).forEach((pane: any) => {
      this.map.createPane(pane.name);
      this.map.getPane(pane.name).style.zIndex = pane.zindex;
    });
  }

  private addZoomBar(): void {
    const zoomBar = new (L as any).control.zoomBar({
      hideZoomToActual: !!this.otherOptions.hideZoomToActual,
      homeCenter: this.center,
      homeZoom: this.zoom,
      getHomeBounds: () => this.homeBounds
    });
    zoomBar.addTo(this.map);
  }

  getAndSetBaseLayers(index: number): void {
    const tileLayers = this.gisdataService.addBaseTileLayers(BASE_LAYER_LIST[LAYER_TYPES.TILE_LAYER], index, this.map);
    this.layers.baselayers = Object.assign(tileLayers, {});
  }

  revalidateMap(): void {
    setTimeout(() => this.map && this.map.invalidateSize(), 200);
  }

  /**
   * Saves the PDF returned by mapService.getRainfallForecastReportByLocation(...).
   * Same as gis-dims-map.downloadReport: the dashboard service calls it right after the report request answers.
   */
  downloadReport(data: Blob, fileName: any): void {
    const filename = fileName + '_Report' + '.pdf';
    const PDF_TYPE = 'application/pdf;charset=utf-8';
    if (!data) {
      this.showThrobber = false;
      return;
    }
    const blob = new Blob([data], { type: PDF_TYPE });
    const objectUrl = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = filename;
    a.target = '_blank';
    a.click();
    setTimeout(() => {
      this.showThrobber = false;
    });
  }

  /** re-fit the current outline (district or block) after the container was resized */
  private refit(): void {
    if (!this.map || this.userMoved) { return; }
    for (const key of ['Block Boundaries', 'District Boundaries']) {
      const o = this.layers.overlaylayers[key];
      const l = o && (typeof o.getBounds === 'function' ? o : o.layer);
      if (l && typeof l.getBounds === 'function') {
        const b = l.getBounds();
        if (b && b.isValid()) { this.map.fitBounds(b, { padding: [10, 10] }); return; }
      }
    }
    if (this.geoLayer) {
      const b = this.geoLayer.getBounds();
      if (b && b.isValid()) { this.map.fitBounds(b, { padding: [10, 10] }); }
    }
  }

  // ------------------------------------------------------------------ layer plumbing used by the map service
  // (same signatures as gis-dims-map)
  addSrcWiseWMSLayers(wmsLayersBasedOnView: any): void {
    this.layers.overlaylayers = Object.assign({}, this.layers.overlaylayers,
      this.gisdataService.setWMSOverlays(this.map, wmsLayersBasedOnView, this.layers.overlaylayers));
  }

  addSrcWiseVECTORLayers(vectorLayersBasedOnView: any): void {
    this.layers.overlaylayers = Object.assign({}, this.layers.overlaylayers,
      this.gisdataService.setVECTOROverlays(this.map, vectorLayersBasedOnView, null,
        this.layers.overlaylayers, this.params, this.queryParams.params, this));
  }

  addSrcWiseGEOJSONLayers(geoJsonLayersBasedOnView: any, queryParams?: any): void {
    if (queryParams) {
      if (this.selectedLocType === queryParams.locType) { return; }
      this.selectedLocType = queryParams.locType;
    }
    this.gisdataService.setGEOJSONOverlays(this.map, geoJsonLayersBasedOnView, this.layers, this.otherOptions, this)
      .then((data: any) => {
        this.layers.overlaylayers = Object.assign({}, this.layers.overlaylayers, data);
        this.manageLegend();
      });
  }

  /** The legend is drawn by this component's template from `scale`; the Leaflet legend control is not used. */
  manageLegend(): void { }
  addLegend(): void { }

  fitBoundsForCurrentLocation(queryParams: any): void { this.pendingMobileAutoFit = false; }

  removeComponentWiseLayers(): void {
    for (const key in this.layers.overlaylayers) {
      const layer = this.layers.overlaylayers[key]['options'];
      if (layer['component'] && layer['component'] === this.params['component']) {
        if (this.map.hasLayer(this.layers.overlaylayers[key])) {
          this.map.removeLayer(this.layers.overlaylayers[key]);
        }
        delete this.layers.overlaylayers[key];
      }
    }
  }

  removeAllLayers(): void {
    for (const key in this.layers.overlaylayers) {
      if (this.map.hasLayer(this.layers.overlaylayers[key])) {
        this.map.removeLayer(this.layers.overlaylayers[key]);
      }
      delete this.layers.overlaylayers[key];
    }
    for (const key in this.layers.processedLayers) {
      const processed = this.layers.processedLayers[key];
      if (processed) {
        for (const elem in processed) {
          if (processed[elem].layerConfig && this.map.hasLayer(processed[elem].layerConfig)) {
            this.map.removeLayer(processed[elem].layerConfig);
          }
        }
      }
      delete this.layers.processedLayers[key];
    }
  }

  removeHeatMapLayers(): void {
    Object.keys(this.layers.aoiLayers).forEach((name: any) => {
      const layer: any = this.layers.aoiLayers[name].options;
      if (layer.component && layer.component === this.params.component) {
        if (this.map.hasLayer(this.layers.aoiLayers[name])) { this.map.removeLayer(this.layers.aoiLayers[name]); }
        delete this.layers.aoiLayers[name];
      }
    });
  }

  // ------------------------------------------------------------------ optional self-drawn choropleth
  colorFor(v: number | undefined): string {
    if (v === undefined || v === null || isNaN(v) || !this.scale || !this.scale.length) { return '#ffffff'; }
    for (const s of this.scale) { if (v < s.max) { return s.color; } }
    return this.scale[this.scale.length - 1].color;
  }

  private prop(f: any, key: string): any {
    return f && f.properties ? f.properties[key] : undefined;
  }

  private styleFor(f: any): any {
    const id = String(this.prop(f, this.idProp));
    const hit = (this.pilotIds && this.pilotIds.indexOf(id) > -1) || this.selectedId === id;
    return {
      fillColor: this.colorFor(this.values ? this.values[id] : undefined),
      fillOpacity: 0.9,
      color: hit ? '#000000' : '#8a8a8a',
      weight: hit ? 3 : 0.8
    };
  }

  private renderGeo(): void {
    if (!this.map) { return; }
    if (this.geoLayer) { this.map.removeLayer(this.geoLayer); this.geoLayer = null; }
    if (!this.geojson) { this.renderLabels(); return; }

    this.geoLayer = (L as any).geoJSON(this.geojson, {
      style: (f: any) => this.styleFor(f),
      onEachFeature: (f: any, layer: any) => {
        const id = String(this.prop(f, this.idProp));
        const name = this.prop(f, this.nameProp);
        layer.bindTooltip(() => this.tooltipText(id, name), { sticky: true, className: 'srkl-tooltip' });
        layer.on('mouseover', () => this.blockHover.emit(id));
        layer.on('mouseout', () => this.blockHover.emit(null));
        layer.on('click', () => this.blockClick.emit(id));
      }
    }).addTo(this.map);

    this.bringHighlightedToFront();
    if (!this.fitted) {
      const b = this.geoLayer.getBounds();
      if (b.isValid()) {
        this.homeBounds = b;
        this.map.fitBounds(b, { padding: [10, 10] });
        this.fitted = true;
      }
    }
    this.renderLabels();
  }

  private refreshStyles(): void {
    if (!this.geoLayer) { return; }
    this.geoLayer.eachLayer((l: any) => l.setStyle(this.styleFor(l.feature)));
    this.bringHighlightedToFront();
  }

  private bringHighlightedToFront(): void {
    this.geoLayer.eachLayer((l: any) => {
      const id = String(this.prop(l.feature, this.idProp));
      if ((this.pilotIds && this.pilotIds.indexOf(id) > -1) || this.selectedId === id) { l.bringToFront(); }
    });
  }

  private tooltipText(id: string, name: string): string {
    const v = this.values ? this.values[id] : undefined;
    if (this.tooltipFn) { return this.tooltipFn(id, name, v); }
    return '<b>' + name + '</b><br>' + (v === undefined ? 'No data' : v + ' mm');
  }

  private renderLabels(): void {
    if (this.labelLayer) { this.map.removeLayer(this.labelLayer); this.labelLayer = null; }
    if (!this.showLabels || !this.geoLayer) { return; }
    this.labelLayer = (L as any).layerGroup();
    this.geoLayer.eachLayer((l: any) => {
      const name = this.prop(l.feature, this.nameProp);
      if (!name) { return; }
      (L as any).marker(l.getBounds().getCenter(), {
        interactive: false,
        icon: (L as any).divIcon({ className: 'srkl-label', html: '<span>' + name + '</span>', iconSize: [0, 0] })
      }).addTo(this.labelLayer);
    });
    this.labelLayer.addTo(this.map);
  }

  // ------------------------------------------------------------------ playbar (built in)
  /** Called when a step is chosen (cell click, prev/next, auto-play). Same name/signature as gis-dims-map. */
  changeTimeLayers(groupName: any): void {
    const idx = this.labelStrings.findIndex(l => l.groupName == groupName);
    if (idx > -1) { this.blockNumber = idx; }
    this.stepChange.emit(String(groupName));
    const fn = this.otherOptions.animationLayers;
    if (this.injectedService && fn && this.injectedService[fn]) {
      this.injectedService[fn](this, groupName);
    }
  }

  private syncPlaybar(): void {
    const idx = this.labelStrings ? this.labelStrings.findIndex(l => l.groupName == this.activeStep) : -1;
    if (idx > -1) { this.blockNumber = idx; this.playPosition = idx; }
  }

  get activeGroup(): any { return this.activeStep; }

  trackByStep = (_: number, l: any) => l.groupName;

  togglePlay(): void { this.playing ? this.stopPlay() : this.startPlay(); }

  private startPlay(): void {
    if (!this.labelStrings || !this.labelStrings.length) { return; }
    this.playing = true;
    this.timer = setInterval(() => this.advance(1), 1500);
  }

  private stopPlay(): void {
    this.playing = false;
    clearInterval(this.timer);
  }

  private advance(dir: number): void {
    const n = this.labelStrings ? this.labelStrings.length : 0;
    if (!n) { return; }
    const i = this.labelStrings.findIndex(l => l.groupName === this.activeStep);
    const next = ((i < 0 ? 0 : i) + dir + n) % n;
    this.changeTimeLayers(this.labelStrings[next].groupName);
  }

  manualStep(dir: number): void { this.stopPlay(); this.advance(dir); }
  pick(groupName: any): void { this.stopPlay(); this.changeTimeLayers(groupName); }

  // ------------------------------------------------------------------ mode
  setMode(m: SrklMode): void {
    if (m !== this.mode) { this.modeChange.emit(m); }
  }

  /** the weather button is highlighted while the current mode belongs to one of its periods */
  isGroupActive(g: SrklModeGroup): boolean {
    return g.periods.some(p => p.mode === this.mode);
  }

  isPeriodActive(p: SrklPeriod): boolean {
    return !!p.mode && p.mode === this.mode;
  }

  /** Daily / 3-Hourly clicked in a popup: switch to its mode (periods without a mode yet do nothing) */
  setPeriod(p: SrklPeriod, ev?: Event): void {
    // drop the focus so the popup closes once the pointer leaves (it is kept open by :focus-within otherwise)
    if (ev && ev.currentTarget && (ev.currentTarget as HTMLElement).blur) { (ev.currentTarget as HTMLElement).blur(); }
    if (p.mode) { this.setMode(p.mode); }
  }

  activeMenu(event: any): void { this.activeSideMenu = event; }

  trackByLabel = (_: number, s: SrklScaleItem) => s.label;
  trackByMode = (_: number, m: { key: string }) => m.key;
  trackByGroup = (_: number, g: SrklModeGroup) => g.key;
}